import { Component } from 'react';
import axios from "axios";
import { CircularProgress } from '@mui/material';
import { WithStyles, withStyles } from '@mui/styles';
import { connect } from 'react-redux';
import { Dispatch, bindActionCreators } from 'redux';
import _ from 'lodash';

// Contexts
import { withTranslate, withTenant } from '../../../core/TenantProvider';
// Components - Atoms, Molecules, Organisms, Pages
import APButton from '../../atoms/APButton';
import APErrorMessage from "../../../components/atoms/APErrorMessage";
import APSection from '../../atoms/APSection';
import APReferralStepPanel from './APReferralStepPanel';
import APAlertMessage from '../../../components/atoms/APAlertMessage';
// Files in the same folder
import APReferralStepStyles from './styles/APReferralStepContainer.style';
// Redux
import { ReducerState } from '../../../redux/modules';
import { actions as SessionActions } from '../../../redux/modules/session';
import { post } from '../../../redux/modules/session/actions';
import { actions as servicesDowntimeAction } from '../../../redux/modules/servicesDowntime';
import { actions as SlidingRightPanelActions } from '../../../redux/modules/uistates';
// Types
import { AffiliateDetailsAttributes } from '../../../core/types/AffiliateDetails';
// Utils
import { TriggerGoogleAnalytics4Tag, TriggerGoogleAnalyticsTag } from '../../../core/utils/GoogleAnalyticTag';
import { GoogleAnalyticTags } from '../../../core/utils/GoogleAnalyticTag/GoogleAnalyticsTags';
import { GoogleAnalytics4Tags } from '../../../core/utils/GoogleAnalyticTag/GoogleAnalytics4Tags';
import { CheckUSAOrCanadaAffiliate } from '../../../core/utils/CheckUSAOrCanadaAffiliate/CheckUSAOrCanadaAffiliate';
import { getConfigProperty } from '../../../core/EnvironmentConfig/EnvironmentConfig';
// Constants
import {
	DEFAULT_LOCALE,
	EU_LOCALE,
	EU_SPECIFIC_LEGAL_ENTITY,
	USA_LOCALE
} from '../../../core/utils/Constants/Constants';

type StepDefinition = {
	sections: any,
	label: string
}

interface APReferralStepProps extends WithStyles<typeof APReferralStepStyles> {
	postWrapper: Function,
	onChange?: Function,
	onComplete?: Function,
	steps: Array<StepDefinition>,
	title: string,
	translate: Function,
	settings: any,
	toggleSlidingRightPanelStatus: Function,
	affiliateDetails: AffiliateDetailsAttributes | null,
	getServicesErrorObj: Function;
	callForTesting?: boolean;
}

interface APReferralStepState {
	isPending: boolean,
	isValid: any,
	isEmailValid: any,
	form: any,
	step: number,
	hideReferralDetails: boolean,
	touched: any,
	loaded: boolean,
	isError: boolean,
	serverError: string;
	showEmail: boolean,
	isAlertToBeShown: boolean
}

class APReferralStep extends Component<APReferralStepProps, APReferralStepState> {
	state = {
		isPending: false,
		isValid: false,
		isEmailValid: false,
		form: {
			receiveEmail: true,
			name: "",
			email: ""
		},
		step: 0,
		hideReferralDetails: false,
		touched: {},
		loaded: true,
		isError: false,
		serverError: "",
		showEmail: true,
		isAlertToBeShown: false
	}

	constructor(props: APReferralStepProps) {
		super(props);

		this.onBack = this.onBack.bind(this);
		this.onBlur = this.onBlur.bind(this);
		this.onChange = this.onChange.bind(this);
		this.onConfirm = this.onConfirm.bind(this);
		this.onContinue = this.onContinue.bind(this);
		this.onValidStateChange = this.onValidStateChange.bind(this);
		this.referralLink = this.referralLink.bind(this);
	}

	referralLink = (affiliateNumber: string = ''): string => {
		const {
			settings
		} = this.props;

		let org: string = settings.ORG_CODE.toLowerCase();
		const ORGANIZATION_DOMAIN_SEND_REGISTRATION_LINK: any = getConfigProperty('srlDomainUrl');
		const siteDomain: string = ORGANIZATION_DOMAIN_SEND_REGISTRATION_LINK[org];

		return `https://${siteDomain}?afflno=${affiliateNumber}`
	};

	onBack() {
		this.setState({
			step: this.state.step - 1,
			isValid: false,
			isEmailValid: false,
			form: {
				receiveEmail: true,
				name: "",
				email: ""
			},
			touched: {
				name: false,
				email: false
			},
			hideReferralDetails: false,
			showEmail: true,
			loaded: true,
			isError: false,
			serverError: "",
		})
	}

	onBlur(evt: any) {
		const {
			target: {
				name
			}
		} = evt;

		this.setState({
			touched: {
				...this.state.touched,
				[name]: true
			}
		});
	}

	onChange(evt: any) {
		const {
			target: {
				checked,
				name,
				type,
				value
			}
		} = evt;

		const resolvedValue = type === 'checkbox' ? checked : value;

		this.setState({
			form: {
				...this.state.form,
				[name]: resolvedValue
			}
		}, () => {
			const {
				onChange
			} = this.props;

			if (onChange) {
				onChange(this.state.form);
			}
		});
	}

	onConfirm() {
		const {
			onComplete
		} = this.props;

		if (onComplete) {
			onComplete(this.state.form);
		}
	}

	onClose = (e) => {
		e.preventDefault();
		this.props.toggleSlidingRightPanelStatus(false, '');
	}

	onContinue() {
		const {
			translate,
			affiliateDetails,
			postWrapper,
			getServicesErrorObj
		} = this.props;

		if (translate('referral.generator.button')) {
			TriggerGoogleAnalyticsTag(
				GoogleAnalyticTags.eventAction.click,
				GoogleAnalyticTags.registerClient.click.referralURLGeneratorGenerateURLCTA
			);
			TriggerGoogleAnalytics4Tag(
				GoogleAnalytics4Tags.referralUrlGenerator.name,
				GoogleAnalytics4Tags.eventAction.click,
				GoogleAnalytics4Tags.referralUrlGenerator.click.referralURLGeneratorGenerateURLCTA
			);
		}


		const sendUrlBase: string = "/v1/affiliate-portal/registration/send"
		const sendEmailEndPoint: string = `${sendUrlBase}email`;
		const isUSAOrCanadaAffiliate: boolean = CheckUSAOrCanadaAffiliate();
		const isEUAffiliate: boolean = affiliateDetails?.affiliate?.legal_entity ? EU_SPECIFIC_LEGAL_ENTITY.includes(affiliateDetails.affiliate.legal_entity) : false;
		const locale: string = isUSAOrCanadaAffiliate ? USA_LOCALE : isEUAffiliate ? EU_LOCALE : DEFAULT_LOCALE;

		const emailData = {
			"receivers_email_address": `${this.state.form.email}`,
			"receiver_name": `${this.state.form.name}`,
			"locale": locale
		}

		this.setState({
			isPending: true
		}, () => {

			const { showEmail } = this.state;
			const postEmailEndPoint = () => postWrapper(sendEmailEndPoint, emailData);

			const setError = (error?: any) => {
				if (error.response.status === 504 && error.response?.data?.code === '1510') {
					this.setState({
						isError: false,
						isPending: false,
						loaded: true,
						isAlertToBeShown: true
					});
				} else {
					getServicesErrorObj(error);
					this.setState({
						isError: true,
						isPending: false,
						loaded: true,
					});
				}

				TriggerGoogleAnalyticsTag(
					GoogleAnalyticTags.eventAction.load,
					GoogleAnalyticTags.registerClient.load.referralURLGeneratorFail
				);
				TriggerGoogleAnalytics4Tag(
					GoogleAnalytics4Tags.referralUrlGenerator.name,
					GoogleAnalytics4Tags.eventAction.load,
					GoogleAnalytics4Tags.referralUrlGenerator.load.referralURLGeneratorFail
				);

			};

			const setSuccess = () => this.setState({
				isError: false,
				isPending: false,
				loaded: true,
				step: this.state.step + 1,
				hideReferralDetails: true
			}, () => {
				TriggerGoogleAnalyticsTag(
					GoogleAnalyticTags.eventAction.load,
					GoogleAnalyticTags.registerClient.load.referralURLGeneratorSuccessfully
				);
				TriggerGoogleAnalytics4Tag(
					GoogleAnalytics4Tags.referralUrlGenerator.name,
					GoogleAnalytics4Tags.eventAction.load,
					GoogleAnalytics4Tags.referralUrlGenerator.load.referralURLGeneratorSuccessfully
				);
			});

			if (showEmail) { // Send email only
				axios.all([postEmailEndPoint()])
					.then(axios.spread((res: any) => {
						(res.status === 200) ? setSuccess() : setError();
					}))
					// .catch(err => setError());
					.catch((error) => {
						setError(error)
					});

			}
		});

	}

	onValidStateChange(valid: boolean, isEmailValid: boolean) {
		this.setState({
			isValid: valid,
			isEmailValid
		});
	}

	render() {
		const {
			classes,
			steps,
			title,
			translate,
			settings,
			affiliateDetails
		} = this.props;

		const {
			step,
			isPending,
			showEmail,
			form,
			isEmailValid
		} = this.state;

		const max = steps.length - 1;

		let validBtn: boolean = false;

		if (form.name.length > 0) {
			const validEmail: boolean = showEmail && isEmailValid;
			validBtn = validEmail;
		}

		return (

			(isPending) ? (
				<div className={classes.slideOutLoader}>
					<div><CircularProgress className={classes.slideOutLoaderSpinner} /></div>
				</div>
			) : (
				<>
					<div className={classes.stepContainer}>
						<h2>{translate('referral.generator.header')}</h2>
						{this.state.hideReferralDetails ? null : (
							<div className={classes.referralFormDetails}>
								<p>{translate('referral.generator.subHeading.line1')}<span>{`${affiliateDetails?.affiliate?.affiliate_Number}`}</span></p>
								<p>{translate('referral.generator.subHeading.line2')}<span>{this.referralLink(affiliateDetails?.affiliate?.affiliate_Number)}</span></p>
							</div>
						)}
						{this.state.isAlertToBeShown &&
							<APAlertMessage
								className={`serverErrorMessage`}
								title={translate('server.timeout.error.title')}
								message={translate('server.timeout.error.message')}
								severity={"error"}
								icon={'error'}
								hideCloseIcon={false}
								onClose={() => {
									this.setState({
										isAlertToBeShown: false
									})
								}}
							/>
						}
						{this.state.isError ? (<APErrorMessage className={`errorMessage`} errorMessage={translate("referral.generator.error")} />) : ("")}

						<div className={classes.stepForm}>
							<APSection title={title}>
								{step < max ? (
									<p className={classes.referralFormParagraph}>
										{translate('referral.generator.paragraph', {
											company_name: settings.ORG_NAME
										})}
									</p>) : null
								}
								<APReferralStepPanel
									definition={steps[step]}
									form={this.state.form}
									onBlur={this.onBlur}
									onChange={this.onChange}
									onValidStateChange={this.onValidStateChange}
									touched={this.state.touched}
									hideReferralDetails={this.state.hideReferralDetails}
									showEmail={this.state.showEmail}
									key={steps[step]}
								/>
							</APSection>
						</div>
					</div>
					<div className={classes.stepButtons}>

						{step < max ? (
							<APButton
								children={translate("referral.generator.button")}
								className={classes.generateButton}
								color="primary"
								disabled={!validBtn}
								variant="contained"
								onClick={this.onContinue}
							/>
						) : null
						}

						{step === max && (
							<>
								<div className={classes.stepButtonsLeft}>
									<APButton
										children={translate("referral.generator.button.close")}
										className={classes.stepButton}
										color="primary"
										variant="outlined"
										onClick={this.onClose}
									/>
								</div>
								<div className={classes.stepButtonsRight}>
									<APButton
										children={translate("referral.generator.button.send.link")}
										className={classes.stepButton}
										color="primary"
										disabled={!this.state.isValid}
										variant="contained"
										onClick={this.onBack}
									/>
								</div>
							</>
						)}
					</div>
				</>
			)
		);
	}
}

const mapStateToProps = (store: ReducerState) => ({
	affiliateDetails: store.user.affiliateDetails
});

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators({
	postWrapper: post,
	clearSession: SessionActions.clearSession,
	toggleSlidingRightPanelStatus: SlidingRightPanelActions.toggleSlidingRightPanelStatus,
	getServicesErrorObj: servicesDowntimeAction.getServicesErrorObj

}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(withTenant(withTranslate(withStyles(APReferralStepStyles)(APReferralStep))));
